/**
 * Cash: what should be there, what was, and every previous count.
 *
 * A shortage has to be legible without accounting knowledge, so the number is
 * large, coloured, and labelled in plain words — "short" rather than "negative
 * variance". The history underneath is what turns a single bad day into a
 * pattern, which is the thing an owner actually acts on.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { EmptyState, Spinner } from '../../components/ui';
import { money, moment, varianceTone } from './format';
import type { CashCountRow, CashSummary } from './types';

export function CashPanel({
  cash,
  currency,
  timezone,
  businessId,
}: {
  cash: CashSummary;
  currency: string;
  timezone: string;
  businessId: string;
}): ReactNode {
  const history = useQuery({
    queryKey: ['cash-counts', businessId],
    queryFn: () => api.get<{ data: CashCountRow[] }>(`/businesses/${businessId}/cash-counts`),
    enabled: Boolean(businessId),
  });

  const tone = varianceTone(cash.counted === null ? null : cash.variance);
  const variance = Number(cash.variance ?? 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-xs text-slate-500">Expected</p>
          <p className="text-lg font-semibold tabular-nums">{money(cash.expected, currency)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-xs text-slate-500">Counted</p>
          <p className="text-lg font-semibold tabular-nums">
            {cash.counted === null ? 'Not counted' : money(cash.counted, currency)}
          </p>
        </div>
        <div
          className={`rounded-xl border p-3 ${
            tone === 'bad'
              ? 'border-rose-200 bg-rose-50 text-rose-900'
              : tone === 'warn'
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : tone === 'good'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                  : 'border-slate-200'
          }`}
        >
          {/* "Short" and "over", not "variance". */}
          <p className="text-xs opacity-70">
            {cash.counted === null ? 'Difference' : variance < 0 ? 'Short by' : variance > 0 ? 'Over by' : 'Difference'}
          </p>
          <p className="text-lg font-semibold tabular-nums">
            {cash.counted === null ? '—' : money(Math.abs(variance), currency)}
          </p>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        {cash.countedBy
          ? `Counted by ${cash.countedBy} · ${moment(cash.countedAt, timezone)}`
          : 'No count recorded for this period.'}
        {cash.uncountedDays > 0 && (
          <> · {cash.uncountedDays} day(s) in this range had no count at all.</>
        )}
      </p>

      <div>
        <p className="mb-2 text-sm font-medium text-slate-900">Previous counts</p>
        {history.isLoading && <Spinner label="Loading counts" />}
        {history.isError && (
          <p className="text-xs text-rose-700">Could not load previous counts.</p>
        )}
        {history.data && history.data.data.length === 0 && (
          <EmptyState
            title="No counts yet"
            description="Counting the drawer at close is what makes a shortage visible."
          />
        )}
        {history.data && history.data.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="py-2 pr-3 font-medium">Day</th>
                  <th className="py-2 pr-3 text-right font-medium">Expected</th>
                  <th className="py-2 pr-3 text-right font-medium">Counted</th>
                  <th className="py-2 pr-3 text-right font-medium">Difference</th>
                  <th className="py-2 pr-3 font-medium">By</th>
                </tr>
              </thead>
              <tbody>
                {history.data.data.map((row) => {
                  const rowVariance = Number(row.variance);
                  return (
                    <tr key={row.id} className="border-b border-slate-100">
                      <td className="py-2 pr-3">{row.counted_for}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {money(row.expected_amount, row.currency_code)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {money(row.counted_amount, row.currency_code)}
                      </td>
                      <td
                        className={`py-2 pr-3 text-right font-medium tabular-nums ${
                          rowVariance < 0
                            ? 'text-rose-700'
                            : rowVariance > 0
                              ? 'text-amber-700'
                              : 'text-emerald-700'
                        }`}
                      >
                        {rowVariance === 0
                          ? 'Exact'
                          : `${rowVariance < 0 ? 'Short ' : 'Over '}${money(Math.abs(rowVariance), row.currency_code)}`}
                      </td>
                      <td className="py-2 pr-3 text-slate-600">{row.counted_by ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
